import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Cost } from "../../../../../../models/cost";
import { CostType } from "../../../../../../models/cost-type";
import { FixCost } from "../../../../../../models/fix-cost";
import { FormulaCost } from "../../../../../../models/formula-cost";
import { CostControllerService } from "../../../../../../services/cost-controller/cost-controller.service";
import { CurrencyControllerService } from "../../../../../../services/currency-controller/currency-controller.service";
import { CostView } from "../../../../../../models/infix-formula/costView";
import { Registration } from "../../../../../../models/registration";
import { CostService } from "../../../../../../services/cost-controller/cost.service";
import { Store } from "../../../../../../services/store.service";
import { PaymentFormControllerService } from "../../../../../../services/form-controller/payment-form-controller.service";
import {TranslatedBaseError} from "../../../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

export interface RecipientAccountRow {
  provider: string;
  accountId: string;
  requireSettlement: boolean;
}

export interface PaymentProviderOption {
  code: string;
  name: string;
  /** Undefined when the backend is too old to declare it — "cannot say", not "no". */
  supportsBeneficiaryAccounts?: boolean;
}

@Component({
  selector: "cost-edit",
  templateUrl: "./cost-edit.component.html",
  styles: [],
  providers: [ToastrService, PaymentFormControllerService],
})
export class CostEditComponent extends TranslatedBaseError implements OnInit, OnChanges, OnDestroy {

  @Input() public serviceId: string;
  @Input() public cost: Cost;
  @Input() public registration: Registration;
  @Input() public costView: CostView;
  @Output() public formSubmit = new EventEmitter();
  @Output() public editCancel = new EventEmitter();
  @Output() public registrationCostFormSubmit = new EventEmitter();
  @Input() public addNew: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };

  public showDeterminantBuilder: boolean = false;

  public currencies: any[] = [];
  public fixValue: number;
  public formulaCost: FormulaCost = new FormulaCost();
  public editOrNew: string;
  public additionalInformation: string;
  public determinants: any[] = [];
  public determinantValues: any[] = [];
  public selectedDeterminantValues: any[] = [];
  public registrationCostList: any = [];
  public disabled = false;
  public tabToShow = "DETAILS";
  public costTypeChanged: boolean = false;
  public recipientAccountRows: RecipientAccountRow[] = [];
  public paymentProviders: PaymentProviderOption[] = [];
  public providersLoaded = false;

  public openFormula: boolean = false;
  public openDeterminantFormula: boolean = false;

  public subscription: any;
  public isLoading: boolean = false;

  constructor(private costControllerService: CostControllerService,
              private currencyControllerService: CurrencyControllerService,
              private costService: CostService,
              private paymentFormControllerService: PaymentFormControllerService,
              public toastr: ToastrService,
              public store: Store,
              public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.subscription = this.costService.editData.subscribe((data) => {
      console.log("CostEditComponent: editData subscription", data);
      this.serviceId = data.serviceId;
      this.registration = data.registration;
      this.cost = data.cost;
      this.costView = data.costView;
      this.addNew = data.addNew;
      this.showDeterminantBuilder = data.showDeterminantBuilder;

      this.initCost();
      this.loadPaymentProviders();
    });
    this.getCurrencies();
  }

  /**
   * Populate the provider picker from the instance's own provider list, so the codes offered are
   * the ones ds-backend will match against PaymentProvider.code — the value is a free-text-shaped
   * string on the wire, and a typo silently resolves to no account at all.
   *
   * Only providers whose adapter declares supports_beneficiary_accounts are offered: "At the
   * withdrawal" is cash at a counter with nothing to settle to, and an online provider such as
   * iVeri never reads the value either. The capability travels with each provider rather than
   * being listed here, because which providers exist differs from one country to the next.
   *
   * On failure the template falls back to a plain text input — a provider lookup being unavailable
   * must not stop an author editing a fee.
   */
  public loadPaymentProviders() {
    if (!this.serviceId || this.providersLoaded) {
      return;
    }
    this.paymentFormControllerService.getPaymentProviders().subscribe(
      (providers: any[]) => {
        const all: PaymentProviderOption[] = (providers || [])
          .filter((provider: any) => provider && provider.code)
          .map((provider: any) => ({
            code: String(provider.code),
            name: String(provider.name || provider.code),
            supportsBeneficiaryAccounts: provider.supportsBeneficiaryAccounts,
          }));
        // An older BPA-backend or ds-backend omits the flag entirely. Absent means "cannot say",
        // not "no" — filtering on it then would empty the picker for the whole rollout window, so
        // fall back to offering everything until at least one provider answers the question.
        const anyDeclared = all.some((provider) => provider.supportsBeneficiaryAccounts !== undefined);
        this.paymentProviders = anyDeclared
          ? all.filter((provider) => provider.supportsBeneficiaryAccounts === true)
          : all;
        this.providersLoaded = true;
      },
      () => {
        // Distinct from "loaded, and none of them take a beneficiary account": only a failed lookup
        // falls back to free text, so an instance with no eligible provider still gets the picker
        // rather than a box inviting a typo.
        this.paymentProviders = [];
        this.providersLoaded = false;
      },
    );
  }

  /**
   * Whether a stored provider code is in the fetched list. The template needs this to decide if a
   * row's own value still needs an <option> of its own: BPA-backend caches the provider list in a
   * static field that is never invalidated, so a provider added after the last restart is missing
   * from it, and without an option the row would silently reset to blank when the drawer opens.
   *
   * Deliberately returns a boolean rather than a per-row options array. A template method that
   * builds a new array is called on every change-detection cycle, and *ngFor's differ treats each
   * new reference as a changed collection — that is an infinite change-detection loop, which hangs
   * the browser rather than failing visibly.
   */
  public isKnownProvider(code: string): boolean {
    return this.paymentProviders.some((option) => option.code === code);
  }

  /**
   * "Settlement required" is a Pesaflow concept (their Create-Invoice require_settlement flag) that
   * decides whether the collected money is paid out to the account. It is true for every row anyone
   * has ever configured, and an accidental un-tick would leave funds collected but unsettled with no
   * visible signal. So it is hidden unless a row already carries false — the capability stays, the
   * trap goes.
   */
  public get showSettlementColumn(): boolean {
    return this.recipientAccountRows.some((row) => row.requireSettlement === false);
  }

  /** Rows with exactly one of provider / accountId filled. A blank row is not an error. */
  public incompleteRecipientAccountRows(): number {
    return this.recipientAccountRows.filter((row) => {
      const hasProvider = !!(row.provider || "").trim();
      const hasAccount = !!(row.accountId || "").trim();
      return hasProvider !== hasAccount;
    }).length;
  }

  public changeFormula(data: any) {
    console.log("CostEditComponent: changeFormula", data);
    this.openFormula = true;
    this.showDeterminantBuilder = true;
    this.costView = data.costView;
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (changes.serviceId && changes.serviceId.currentValue) {
      this.serviceId = changes.serviceId.currentValue;
    }
    if (changes.cost) {
      if (changes.cost.currentValue) {
        this.cost = changes.cost.currentValue;
      } else {
        this.cost = new Cost();
      }
      this.initCost();
    }
  }

  public initCost() {
    if (this.cost != null && this.cost.costType != null) {
      if (this.cost.costType === CostType.FIX) {
        const c = this.cost as FixCost;
        this.fixValue = c.fixValue;
      } else if (this.cost.costType === CostType.FORMULA) {
        const c = Object.assign(new FormulaCost(), this.cost);
        this.formulaCost = c;
      }
      this.costTypeChanged = false;
    }
    this.initRecipientAccounts();
  }

  public initRecipientAccounts() {
    this.recipientAccountRows = [];
    if (!this.cost || !this.cost.recipientAccounts) {
      return;
    }
    try {
      const parsed = JSON.parse(this.cost.recipientAccounts);
      Object.keys(parsed || {}).forEach((provider) => {
        const entry = parsed[provider] || {};
        this.recipientAccountRows.push({
          provider,
          accountId: entry.accountId || "",
          requireSettlement: entry.requireSettlement !== false,
        });
      });
    } catch (e) {
      // A value we cannot parse is shown as "no accounts" rather than breaking the drawer; the
      // author can re-enter it. Saving then overwrites the unparseable value.
      this.recipientAccountRows = [];
    }
  }

  public serialiseRecipientAccounts(): string {
    const map: any = {};
    let count = 0;
    this.recipientAccountRows.forEach((row) => {
      // ds-backend matches the provider key against PaymentProvider.code, which is always lowercase
      // (a Django TextChoices). "Pesaflow" or "pesaflow " would silently fail to match, so normalise
      // here rather than relying on the author to type it exactly right. accountId is trimmed too —
      // trailing whitespace is equally silent — but not case-folded: account identifiers are opaque.
      const provider = (row.provider || "").trim().toLowerCase();
      const accountId = (row.accountId || "").trim();
      if (provider && accountId) {
        map[provider] = { accountId, requireSettlement: row.requireSettlement !== false };
        count++;
      }
    });
    return count === 0 ? null : JSON.stringify(map);
  }

  public addRecipientAccountRow() {
    this.recipientAccountRows.push({ provider: "", accountId: "", requireSettlement: true });
  }

  public removeRecipientAccountRow(index: number) {
    this.recipientAccountRows.splice(index, 1);
  }

  public save() {
    // Abort rather than drop silently. serialiseRecipientAccounts skips a half-filled row, so
    // without this an author who fills only the account id gets a green "Cost saved!" and no
    // account — and half-clearing an existing good row would wipe a working mapping the same way.
    if (this.incompleteRecipientAccountRows() > 0) {
      this.simpleErrorMessage("annexes.costs.recipient_incomplete");
      return;
    }
    const costType: CostType = this.cost.costType;
    let cost: Cost;
    if (costType === CostType.FIX) {
      cost = new FixCost();
      (cost as FixCost).fixValue = this.fixValue;
    } else if (costType === CostType.FORMULA) {
      cost = new FormulaCost();
      (cost as FormulaCost).formulaCostItems = (this.cost as FormulaCost).formulaCostItems;
    } else {
      // TODO: Throw error…
    }
    cost.id = this.cost.id;
    cost.name = this.cost.name;
    cost.currencyId = this.cost.currencyId;
    cost.costType = costType;
    cost.jsonDeterminants = this.cost.jsonDeterminants;
    cost.recipientAccounts = this.serialiseRecipientAccounts();

    if (this.cost.id === undefined || this.costTypeChanged) {
      this.createCost(cost);
    } else {
      this.saveCost(cost);
    }
  }

  public createCost(cost: Cost) {
    if (!this.isLoading) {
      this.isLoading = true;
      const dataCb = (data: Cost) => {
        if (data.costType === CostType.FORMULA) {
          data = Object.assign(new FormulaCost(), data);
        }
        this.formSubmit.emit({ cost: data, create: true });
        this.isLoading = false;
      };
      const errorCb = (error: any) => { this.showErrorMessage(error); this.isLoading = false; };
      if (cost.costType === CostType.FIX) {
        this.costControllerService.createFixCost(this.registration.id, cost as FixCost).subscribe(dataCb, errorCb);
      } else if (cost.costType === CostType.FORMULA) {
        this.costControllerService.createFormulaCost(this.registration.id, cost as FormulaCost).subscribe(dataCb, errorCb);
      }
    }
  }

  public saveCost(cost: Cost) {
    if (!this.isLoading) {
      this.isLoading = true;
      const dataCb = () => {
        this.responseSuccessful("Cost saved!");
        this.formSubmit.emit({ save: true });
        this.isLoading = false;
      };
      const errorCb = (error: any) => this.showErrorMessage(error);
      if (cost.costType === CostType.FIX) {
        this.costControllerService.saveFixCost(cost as FixCost).subscribe(dataCb, errorCb);
      } else if (cost.costType === CostType.FORMULA) {
        this.costControllerService.saveFormulaCost(cost as FormulaCost).subscribe(dataCb, errorCb);
      }
    }
  }

  public getCurrencies() {
    this.currencyControllerService.getEnabledCurrencies().subscribe(
      (data) => {
        this.currencies = data;
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public isFixCost(): boolean {
    return this.cost.costType === CostType.FIX;
  }

  public isFormulaCost(): boolean {
    return this.cost.costType === CostType.FORMULA;
  }

  public resetForm() {
    this.cost = null;
  }

  public closeLayer() {
    this.editCancel.emit({ cancel: true });
    this.resetForm();
  }

  public closeDetLayer() {
    this.store.state.common.determinantSection.reset();
  }

  public costTypeChangedEvent(costType: CostType) {
    this.costTypeChanged = true;
  }

  appendDeterminant = (det) => {
    let data = JSON.parse(this.cost.jsonDeterminants);
    if (!Array.isArray(data)) {
      data = [];
    }
    if (data.length === 0) {
      data.push({
        type: 'OR',
        items: []
      });
    }
    let lastItem = data[data.length - 1];
    if (lastItem.items == null) {
      lastItem = {
        type: 'OR',
        items: []
      };
      data.push(lastItem);
    }
    lastItem.items.push({
      type: det.type || 'OR',
      determinantId: det.determinantId,
    });
    this.cost.jsonDeterminants = JSON.stringify(data);
  };
}
